import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import {
  useColorMode, useColorModeValue, Box, Container, Text, Flex,
  Icon, SimpleGrid, Image, Button, VStack, HStack, Heading,
} from "@chakra-ui/react";
import { FiCheckCircle, FiXCircle, FiGlobe } from "react-icons/fi";
import GodLevelCourses from "./GodSoftwareEngineering.jsx";
import dsacourse from "../../assets/images/DSA.png"
import dsaintensive from "../../assets/images/DSAIntensive.png";
import aptitude from "../../assets/images/Aptitude.png";
import dbms from "../../assets/images/DBMS.png";
import {
  fetchJavaRoadmapEpisodes,
  fetchAptitudeRoadmapEpisodes,
  fetchDbmsRoadmapEpisodes,
} from "./Googlesheets";


const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');`;

/* Certificate application form for the Java DSA roadmap course */
const JAVA_ROADMAP_CERTIFICATE_LINK = "https://docs.google.com/forms/d/e/1FAIpQLSe16BU5kOVb8gA8wxBQKU9e-qwavl34dMBA0QL7YxiHSyu02w/viewform?usp=sharing&ouid=105982408602728121042";

/* ─── FREE / PAID YOUTUBE-STYLE COURSE DATA ─────────────────────────────────── */
/* Every free, roadmap-style course shares the same modal shape — a fetch fn
   that pulls episode rows from a Google Sheet, a total episode count, and
   how many episodes are grouped per module in the accordion. */
const JAVA_COURSE = {
  key: "java",
  title: "Free JAVA + DSA Course in Telugu",
  tagline: "300 Episodes. Zero Cost. Full Send.",
  desc: "The complete Data Structures & Algorithms roadmap — 300 episodes, dropping weekly, entirely free on YouTube. Built for learners who want the full grind without the price tag.",
  img: dsacourse,
  link: "https://www.youtube.com/watch?v=5OlZDXaPHoQ&list=PLNgUoiw7tLC5xWM6UXxWSi1wkULAY-09M",
  badge: "300-Episode Roadmap",
  status: "Ongoing",
  language: "Telugu",
  price: "FREE",
  accent: "#EF4444",
  totalEpisodes: 300,
  episodesPerModule: 30,
  fetchEpisodes: fetchJavaRoadmapEpisodes,
  modalDescription: "Every episode in the 300-episode plan has a slot below, grouped into modules of 30. New episodes — with notes, problems, and the video — are added every week as they're recorded.",
  certificateLink: JAVA_ROADMAP_CERTIFICATE_LINK,
  bullets: [
    "300 episodes, new drops every week",
    "Free certificate on completion",
  ],
};

const APTITUDE_COURSE = {
  key: "aptitude",
  title: "Free Complete Aptitude in 30 Days Course in Telugu",
  tagline: "30 Episodes. 30 Days. Zero Cost.",
  desc: "A focused, day-by-day aptitude roadmap covering everything from quant to reasoning — 30 episodes, entirely free on YouTube. Built to get you interview-ready in a month.",
  img: aptitude,
  link: "https://www.youtube.com/watch?v=5OlZDXaPHoQ&list=PLNgUoiw7tLC5xWM6UXxWSi1wkULAY-09M",
  badge: "30-Day Roadmap",
  status: "Ongoing",
  language: "Telugu",
  price: "FREE",
  accent: "#8B5CF6",
  totalEpisodes: 30,
  episodesPerModule: 30,
  fetchEpisodes: fetchAptitudeRoadmapEpisodes,
  modalDescription: "All 30 episodes of the Aptitude roadmap, day by day. Each slot below fills in with notes, problems, and the video as it's recorded.",
  bullets: [
    "30 episodes, one for every day",
    "Covers quant, reasoning & verbal",
  ],
};

const DBMS_COURSE = {
  key: "dbms",
  title: "Free DBMS in 30 Days Course in Telugu",
  tagline: "30 Episodes. 30 Days. Zero Cost.",
  desc: "A complete Database Management Systems roadmap in 30 days — 30 episodes, entirely free on YouTube. Everything you need for placements and core-CS interviews.",
  img: dbms,
  link: "https://www.youtube.com/watch?v=5OlZDXaPHoQ&list=PLNgUoiw7tLC5xWM6UXxWSi1wkULAY-09M",
  badge: "30-Day Roadmap",
  status: "Ongoing",
  language: "Telugu",
  price: "FREE",
  accent: "#0EA5E9",
  totalEpisodes: 30,
  episodesPerModule: 30,
  fetchEpisodes: fetchDbmsRoadmapEpisodes,
  modalDescription: "All 30 episodes of the DBMS roadmap, day by day. Each slot below fills in with notes, problems, and the video as it's recorded.",
  bullets: [
    "30 episodes, one for every day",
    "Covers normalization, SQL, transactions & more",
  ],
};

const FREE_COURSES = [JAVA_COURSE, APTITUDE_COURSE, DBMS_COURSE];

const DSA_INTENSIVE = {
  title: "DSA Intensive: 300 in 60 Days",
  tagline: "300 LeetCode Questions. 60 Days. No Excuses.",
  desc: "A live, non-stop 60-day sprint where we solve 300 hand-picked LeetCode questions together — 5 every single day. Can't make a session live? Every class is recorded, so you never fall behind.",
  img: dsaintensive,
  price: "₹1,699",
  language: "Telugu",
  startDate: "August 04, 2026",
  buttonId: "pl_TGymw0kukirwcj",
  seatsTotal: 50,
  seatsFilled: 50,
  features: [
    "300 LeetCode questions solved, start to finish",
    "Live daily classes — recordings included",
    "60 days, completely non-stop",
    "Certificate on completion",
  ],
};

const STATUS_META = {
  Ongoing: { color: "green", label: "Ongoing" },
  Paused:  { color: "orange", label: "Paused" },
  Stopped: { color: "gray",   label: "Stopped" },
};

/* ─── ICONS ────────────────────────────────────────────────────────────────── */
const CloseIcon  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const ClockIcon  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const ChevronDown = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;
const ArrowIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const CertIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5"/></svg>;


/* ─── GENERIC EPISODE ROADMAP MODAL ─────────────────────────────────────────
   Works for any free roadmap course (Java/300, Aptitude/30, DBMS/30 …) —
   just pass the course config in. Episodes that come back as "NA" (or
   blank) for a given link/field are simply not rendered — see `hasValue`.
*/
function RoadmapModal({ course, onClose, isDark }) {
  const { title, modalDescription, totalEpisodes, episodesPerModule, fetchEpisodes, accent, badge } = course;

  const [episodes, setEpisodes] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [openModule, setOpenModule] = useState(1);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    setEpisodes([]);
    fetchEpisodes()
      .then((data) => {
        if (!alive) return;
        setEpisodes(Array.isArray(data) ? data : []);
        setStatus("ready");
      })
      .catch((err) => {
        console.error(`Failed to load "${title}" episodes from Google Sheets:`, err);
        if (alive) setStatus("error");
      });
    return () => { alive = false; };
  }, [fetchEpisodes, title]);

  // Treat missing values AND the literal string "NA" (any case/spacing) as absent —
  // empty/unfilled sheet cells may come back as "" or "NA" depending on how the
  // sheet is filled in.
  const hasValue = (val) => {
    if (val === undefined || val === null) return false;
    const v = String(val).trim();
    return v !== "" && v.toUpperCase() !== "NA";
  };

  // Group fetched rows by Episode number (sheet column is still "Day",
  // now exposed on each row as `day` by googleSheets.js)
  const byEpisode = useMemo(() => {
    const map = {};
    episodes.forEach((ep) => {
      const n = parseInt(ep.day, 10);
      if (!n) return;
      if (!map[n]) map[n] = [];
      map[n].push(ep);
    });
    return map;
  }, [episodes]);

  // "Live" means the episode actually has a real video link uploaded — NOT
  // just that a row exists for that day. Aptitude/DBMS pre-fill all 30 rows
  // with titles up front, so a row existing only tells you the topic is
  // planned; EpisodeLink still reading "NA" means it hasn't been recorded
  // yet. Java's sheet happens to only add a row once it's recorded, so this
  // check is a no-op there — but it's the correct check for both.
  const liveEpisodeNumbers = useMemo(() => {
    const set = new Set();
    episodes.forEach((ep) => {
      const n = parseInt(ep.day, 10);
      if (!n) return;
      if (hasValue(ep.episodeLink)) set.add(n);
    });
    return set;
  }, [episodes]);

  const filledEpisodes = liveEpisodeNumbers.size;
  const latestEpisode = filledEpisodes ? Math.max(...liveEpisodeNumbers) : 0;

  // Once data loads, auto-open whichever module contains the most recent episode
  useEffect(() => {
    if (latestEpisode > 0) setOpenModule(Math.ceil(latestEpisode / episodesPerModule));
  }, [latestEpisode, episodesPerModule]);

  const totalModules = Math.ceil(totalEpisodes / episodesPerModule);
  const modules = useMemo(() => Array.from({ length: totalModules }, (_, m) => {
    const startEp = m * episodesPerModule + 1;
    const endEp = Math.min(startEp + episodesPerModule - 1, totalEpisodes);
    const eps = [];
    for (let e = startEp; e <= endEp; e++) eps.push(e);
    return { module: m + 1, startEp, endEp, eps };
  }), [totalModules, episodesPerModule, totalEpisodes]);

  // Theming — explicit (non-CSS-var) light/dark tokens
  const bg       = isDark ? "#1c1c2e" : "#ffffff";
  const text     = isDark ? "rgba(255,255,255,0.93)" : "#111827";
  const muted    = isDark ? "rgba(255,255,255,0.52)" : "#6B7280";
  const border   = isDark ? "rgba(255,255,255,0.1)"  : "#E5E7EB";
  const rowBg    = isDark ? "rgba(255,255,255,0.04)" : "#F9FAFB";
  const closeBg  = isDark ? "rgba(255,255,255,0.08)" : "#F3F4F6";
  const closeClr = isDark ? "rgba(255,255,255,0.7)"  : "#374151";

  const linkChipStyle = (color) => ({
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 600,
    color, background: color + "14", border: `1px solid ${color}33`, borderRadius: 999,
    padding: "3px 10px", textDecoration: "none",
  });

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: bg, border: `1px solid ${border}`, borderRadius: 22, width: "100%", maxWidth: 640, maxHeight: "88vh", overflowY: "auto", padding: "38px 36px 40px", position: "relative", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}
      >
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, borderRadius: "22px 22px 0 0", background: accent }} />

        <button onClick={onClose} style={{ position: "absolute", top: 18, right: 18, background: closeBg, border: "none", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: closeClr }}>
          <CloseIcon />
        </button>

        {/* Header */}
        <div style={{ marginBottom: 20, paddingTop: 6 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ background: accent + "18", color: accent, fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 999, border: `1px solid ${accent}33` }}>{badge}</span>
            <span style={{ background: rowBg, color: muted, fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 999, border: `1px solid ${border}`, display: "flex", alignItems: "center", gap: 5 }}><ClockIcon /> Updated weekly</span>
          </div>
          <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.25rem, 3vw, 1.6rem)", color: text, margin: "0 0 11px", lineHeight: 1.2, fontWeight: 800 }}>{title}</h3>
          <p style={{ fontSize: 14, color: muted, lineHeight: 1.75, margin: "0 0 16px" }}>
            {modalDescription}
          </p>

          {/* Progress */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: muted, marginBottom: 6, fontWeight: 600 }}>
              <span>{status === "ready" ? `${filledEpisodes} of ${totalEpisodes} episodes live` : "Checking progress…"}</span>
              {status === "ready" && <span>{Math.round((filledEpisodes / totalEpisodes) * 100)}%</span>}
            </div>
            <div style={{ height: 6, borderRadius: 999, background: rowBg, border: `1px solid ${border}`, overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 999, background: accent, width: status === "ready" ? `${Math.max((filledEpisodes / totalEpisodes) * 100, filledEpisodes ? 2 : 0)}%` : "0%", transition: "width 0.4s ease" }} />
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: border, margin: "0 0 20px" }} />

        {/* Body states */}
        {status === "loading" && (
          <p style={{ fontSize: 13.5, color: muted, textAlign: "center", padding: "20px 0" }}>Loading the live schedule…</p>
        )}

        {status === "error" && (
          <p style={{ fontSize: 13.5, color: muted, textAlign: "center", padding: "20px 0" }}>
            Couldn't load the live schedule right now — please try again in a bit.
          </p>
        )}

        {status === "ready" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {modules.map((mod) => {
              const isOpen = openModule === mod.module;
              const liveCount = mod.eps.filter((e) => liveEpisodeNumbers.has(e)).length;
              return (
                <div key={mod.module} style={{ border: `1px solid ${border}`, borderRadius: 13, overflow: "hidden" }}>
                  {totalModules > 1 && (
                    <button
                      onClick={() => setOpenModule(isOpen ? null : mod.module)}
                      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "13px 16px", background: isOpen ? accent + "0d" : rowBg, border: "none", cursor: "pointer", textAlign: "left" }}
                    >
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: text }}>
                        Module {mod.module} <span style={{ color: muted, fontWeight: 500 }}>· Episode {mod.startEp}–{mod.endEp}</span>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: liveCount ? accent : muted, background: liveCount ? accent + "15" : "transparent", padding: liveCount ? "2px 9px" : 0, borderRadius: 999 }}>
                          {liveCount}/{mod.eps.length} live
                        </span>
                        <span style={{ color: muted, display: "flex", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.18s" }}><ChevronDown /></span>
                      </span>
                    </button>
                  )}

                  <AnimatePresence initial={false}>
                    {(isOpen || totalModules === 1) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }}
                        style={{ overflow: "hidden" }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 16px 16px" }}>
                          {mod.eps.map((e) => {
                            const eps = byEpisode[e];
                            if (!eps || !eps.length) {
                              return (
                                <div key={e} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10, border: `1.5px dashed ${border}` }}>
                                  <span style={{ flexShrink: 0, width: 72, textAlign: "center", fontSize: 11, fontWeight: 700, color: muted }}>Ep {e}</span>
                                  <span style={{ fontSize: 12.5, color: muted, fontStyle: "italic" }}>Will be updated soon</span>
                                </div>
                              );
                            }
                            return eps.map((row, i) => (
                              <div key={`${e}-${i}`} style={{ display: "flex", gap: 12, padding: "12px 14px", borderRadius: 10, background: rowBg, border: `1px solid ${border}` }}>
                                <span style={{ flexShrink: 0, width: 72, height: 26, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: accent, background: accent + "15", borderRadius: 8 }}>Ep {e}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {hasValue(row.title) || hasValue(row.episodeNo) ? (
                                    <p style={{ margin: "0 0 7px", fontSize: 13.5, fontWeight: 700, color: text, lineHeight: 1.4 }}>
                                      {hasValue(row.title) ? row.title : `Episode ${row.episodeNo}`}
                                    </p>
                                  ) : null}
                                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                                    {hasValue(row.episodeLink) && <a href={row.episodeLink} target="_blank" rel="noopener noreferrer" style={linkChipStyle(accent)}>▶ Watch</a>}
                                    {hasValue(row.notesLink) && <a href={row.notesLink} target="_blank" rel="noopener noreferrer" style={linkChipStyle(isDark ? "#60A5FA" : "#2563EB")}>📓 Notes</a>}
                                    {hasValue(row.problemsLink) && <a href={row.problemsLink} target="_blank" rel="noopener noreferrer" style={linkChipStyle(isDark ? "#34D399" : "#059669")}>🧩 Problems</a>}
                                  </div>
                                </div>
                              </div>
                            ));
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ─── COMPACT COURSE CARDS ──────────────────────────────────────────────────
   Design notes:
   • Image sits on top at its natural aspect ratio (h="auto") with nothing
     overlaid on it — badges, play button etc. all live in the content area
     so the artwork is always 100% visible.
   • Smaller radius, a hairline border and a soft shadow instead of the heavy
     "2xl" shadow; hover just tints the border with the course accent.
   • Content is tightened: one title, one-line tagline, small meta chips,
     short description, compact bullets, one primary CTA (+ a slim
     certificate link where applicable).
*/
const MotionBox = motion(Box);

/* Slim, theme-aware scrollbar for the scrollable card body */
const scrollSx = (thumb) => ({
  scrollbarWidth: "thin",
  scrollbarColor: `${thumb} transparent`,
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: thumb, borderRadius: "999px" },
});

/* Max height of the scrollable text area inside each card */
const CARD_BODY_MAX_H = "170px";

function MetaChip({ children, color, bg }) {
  return (
    <Flex
      align="center"
      gap={1}
      px={2}
      py="2px"
      borderRadius="md"
      fontSize="11px"
      fontWeight="600"
      color={color}
      bg={bg}
      lineHeight="1.5"
    >
      {children}
    </Flex>
  );
}

function FreeCourseCard({ course, onViewRoadmap }) {
  const cardBg = useColorModeValue("white", "gray.900");
  const cardBorderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const imgFrameBg = useColorModeValue("gray.50", "blackAlpha.500");
  const titleColor = useColorModeValue("gray.900", "whiteAlpha.900");
  const bodyColor = useColorModeValue("gray.600", "gray.400");
  const chipBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const chipColor = useColorModeValue("gray.700", "gray.300");
  const certColor = useColorModeValue("green.600", "green.300");
  const thumb = useColorModeValue("#CBD5E0", "rgba(255,255,255,0.28)");
  const meta = STATUS_META[course.status] || STATUS_META.Ongoing;

  return (
    <MotionBox
      whileHover={{ y: -3 }}
      transition={{ duration: 0.18 }}
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="sm"
      border="1px solid"
      borderColor={cardBorderColor}
      _hover={{ borderColor: course.accent, boxShadow: `0 10px 30px ${course.accent}26` }}
      h="full"
      display="flex"
      flexDirection="column"
      cursor="pointer"
      onClick={onViewRoadmap}
    >
      {/* Image — fully visible, natural ratio, nothing on top of it */}
      <Box bg={imgFrameBg} lineHeight={0} borderBottom="1px solid" borderColor={cardBorderColor}>
        <Image src={course.img} alt={course.title} w="full" h="auto" display="block" />
      </Box>

      <Flex direction="column" flex="1" minH={0} maxH={CARD_BODY_MAX_H} overflowY="auto" px={4} pt={3} pb={2} gap={2.5} sx={scrollSx(thumb)}>
        {/* Meta chips */}
        <HStack spacing={1.5} flexWrap="wrap">
          <MetaChip color="white" bg={course.accent}>Free</MetaChip>
          <MetaChip color={chipColor} bg={chipBg}>
            <Icon as={FiGlobe} boxSize={3} /> {course.language}
          </MetaChip>
          <MetaChip color={`${meta.color}.500`} bg={chipBg}>
            <Box as="span" w="6px" h="6px" borderRadius="full" bg={`${meta.color}.400`} display="inline-block" />
            {meta.label}
          </MetaChip>
        </HStack>

        {/* Title + tagline */}
        <Box>
          <Heading
            as="h3"
            fontSize="md"
            fontFamily="Bricolage Grotesque"
            lineHeight="1.25"
            color={titleColor}
            noOfLines={2}
            mb={1}
          >
            {course.title}
          </Heading>
          <Text fontSize="xs" color={course.accent} fontWeight="600">
            {course.tagline}
          </Text>
        </Box>

        <Text fontSize="xs" color={bodyColor} lineHeight="1.6">
          {course.desc}
        </Text>

        {/* Bullets */}
        <VStack align="stretch" spacing={1}>
          {course.bullets.map((b, i) => (
            <Flex key={i} align="flex-start" gap={2} fontSize="xs" color={bodyColor}>
              <Icon as={FiCheckCircle} color={course.accent} mt="2px" flexShrink={0} />
              <Text>{b}</Text>
            </Flex>
          ))}
        </VStack>

      </Flex>

      {/* Footer stays pinned — only the text above scrolls */}
      <Box flexShrink={0}>
        <VStack spacing={1} px={4} pb={3} pt={2}>
          <Button
            onClick={(e) => { e.stopPropagation(); onViewRoadmap(); }}
            w="full"
            size="sm"
            h="34px"
            borderRadius="xl"
            fontWeight="700"
            fontSize="sm"
            color="white"
            bg={course.accent}
            _hover={{ filter: "brightness(0.92)" }}
            _active={{ filter: "brightness(0.85)" }}
            rightIcon={<ArrowIcon />}
          >
            View {course.totalEpisodes}-episode roadmap
          </Button>
          {course.certificateLink && (
            <Button
              as="a"
              href={course.certificateLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              w="full"
              size="sm"
              h="32px"
              variant="ghost"
              fontWeight="600"
              fontSize="xs"
              color={certColor}
              leftIcon={<CertIcon />}
              _hover={{ bg: chipBg }}
            >
              Apply for free certificate
            </Button>
          )}
        </VStack>
      </Box>
    </MotionBox>
  );
}

function DsaIntensiveCard() {
  const cardBg = useColorModeValue("white", "gray.900");
  const cardBorderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const imgFrameBg = useColorModeValue("gray.50", "blackAlpha.500");
  const titleColor = useColorModeValue("gray.900", "whiteAlpha.900");
  const bodyColor = useColorModeValue("gray.600", "gray.400");
  const chipBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const chipColor = useColorModeValue("gray.700", "gray.300");
  const fullBg = useColorModeValue("red.50", "whiteAlpha.100");
  const fullBorder = useColorModeValue("red.100", "red.800");
  const fullText = useColorModeValue("red.600", "red.300");
  const trackBg = useColorModeValue("red.100", "red.900");
  const thumb = useColorModeValue("#CBD5E0", "rgba(255,255,255,0.28)");

  const pct = Math.round((DSA_INTENSIVE.seatsFilled / DSA_INTENSIVE.seatsTotal) * 100);

  return (
    <MotionBox
      whileHover={{ y: -3 }}
      transition={{ duration: 0.18 }}
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="sm"
      border="1px solid"
      borderColor={cardBorderColor}
      h="full"
      display="flex"
      flexDirection="column"
    >
      {/* Image — fully visible, full opacity, nothing on top of it */}
      <Box bg={imgFrameBg} lineHeight={0} borderBottom="1px solid" borderColor={cardBorderColor}>
        <Image src={DSA_INTENSIVE.img} alt={DSA_INTENSIVE.title} w="full" h="auto" display="block" />
      </Box>

      <Flex direction="column" flex="1" minH={0} maxH={CARD_BODY_MAX_H} overflowY="auto" px={4} pt={3} pb={2} gap={2.5} sx={scrollSx(thumb)}>
        <HStack spacing={1.5} flexWrap="wrap">
          <MetaChip color="white" bg="red.500">
            <Icon as={FiXCircle} boxSize={3} /> Registrations full
          </MetaChip>
          <MetaChip color={chipColor} bg={chipBg}>
            <Icon as={FiGlobe} boxSize={3} /> {DSA_INTENSIVE.language}
          </MetaChip>
          <MetaChip color={chipColor} bg={chipBg}>Starts {DSA_INTENSIVE.startDate}</MetaChip>
        </HStack>

        <Box>
          <Heading
            as="h3"
            fontSize="md"
            fontFamily="Bricolage Grotesque"
            lineHeight="1.25"
            color={titleColor}
            noOfLines={2}
            mb={1}
          >
            {DSA_INTENSIVE.title}
          </Heading>
          <Text fontSize="xs" color="red.500" fontWeight="600">
            {DSA_INTENSIVE.tagline}
          </Text>
        </Box>

        <Text fontSize="xs" color={bodyColor} lineHeight="1.6">
          {DSA_INTENSIVE.desc}
        </Text>

        <VStack align="stretch" spacing={1}>
          {DSA_INTENSIVE.features.map((item, idx) => (
            <Flex key={idx} align="flex-start" gap={2} fontSize="xs" color={bodyColor}>
              <Icon as={FiCheckCircle} color="red.400" mt="2px" flexShrink={0} />
              <Text>{item}</Text>
            </Flex>
          ))}
        </VStack>

      </Flex>

      {/* Footer stays pinned — only the text above scrolls */}
      <Flex direction="column" gap={2} px={4} pb={3} pt={2} flexShrink={0}>
        {/* Compact seats meter replacing the old big banner */}
        <Box bg={fullBg} border="1px solid" borderColor={fullBorder} borderRadius="xl" px={3} py={2}>
          <Flex justify="space-between" align="baseline" mb={1.5}>
            <Text fontSize="xs" fontWeight="700" color={fullText}>Seats filled</Text>
            <Text fontSize="sm" fontWeight="800" fontFamily="Bricolage Grotesque" color={fullText}>
              {DSA_INTENSIVE.seatsFilled}/{DSA_INTENSIVE.seatsTotal}
            </Text>
          </Flex>
          <Box h="6px" borderRadius="full" bg={trackBg} overflow="hidden">
            <Box h="full" w={`${pct}%`} bg="red.500" borderRadius="full" />
          </Box>
        </Box>

        <Button
          isDisabled
          w="full"
          size="sm"
          h="34px"
          borderRadius="xl"
          fontWeight="700"
          fontSize="sm"
          colorScheme="red"
          variant="solid"
          opacity={1}
          cursor="not-allowed"
          _hover={{}}
          _active={{}}
          leftIcon={<Icon as={FiXCircle} />}
        >
          No more enrollments
        </Button>
      </Flex>
    </MotionBox>
  );
}

/* ─── MAIN PAGE ────────────────────────────────────────────────────────────── */
export default function BootcampsPage() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  // One "open modal" key instead of one boolean per course — scales cleanly
  // as more free roadmap courses get added.
  const [openCourseKey, setOpenCourseKey] = useState(null);
  const activeCourse = FREE_COURSES.find((c) => c.key === openCourseKey) || null;

  // All tokens are explicit — no CSS variable ambiguity
  const pageBg     = isDark ? "#09090f"                : "#F8F9FB";
  const heroBorder = isDark ? "rgba(255,255,255,0.07)" : "#E5E7EB";
  const heroGlow   = isDark ? "rgba(2,142,196,0.12)"   : "rgba(2,142,196,0.06)";
  const textPri    = isDark ? "rgba(255,255,255,0.93)" : "#111827";
  const textMuted  = isDark ? "rgba(255,255,255,0.48)" : "#6B7280";
  const pillBg     = isDark ? "rgba(2,142,196,0.12)"   : "rgba(2,142,196,0.08)";
  const pillBorder = isDark ? "rgba(2,142,196,0.35)"   : "rgba(2,142,196,0.22)";
  const statBg     = isDark ? "rgba(255,255,255,0.04)" : "#ffffff";
  const statBorder = isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB";

  return (
    <>
      <style>{`
        ${FONT_LINK}
        * { box-sizing: border-box; }
        .bp-page { font-family: 'DM Sans', sans-serif; }
      `}</style>

      <div className="bp-page" style={{ background: pageBg, minHeight: "100vh", padding: "0 0 100px", color: textPri }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <div style={{ textAlign: "center", padding: "80px 24px 64px", background: `radial-gradient(ellipse 80% 45% at 50% 0%, ${heroGlow} 0%, transparent 70%)`, borderBottom: `1px solid ${heroBorder}` }}>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: pillBg, border: `1px solid ${pillBorder}`, color: "#028EC4", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", padding: "6px 16px", borderRadius: 999, marginBottom: 22 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#028EC4" }} />
              Industry-Ready Bootcamps
            </span>

            <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(2.5rem, 6.5vw, 4.8rem)", fontWeight: 800, lineHeight: 1.06, margin: "0 0 18px", color: textPri }}>
              Our Amazing{" "}
              <span style={{ background: "linear-gradient(135deg, #028EC4, #38BDF8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Bootcamps</span>
            </h1>

            <p style={{ fontSize: "clamp(14px, 2vw, 16.5px)", color: textMuted, maxWidth: 490, margin: "0 auto 38px", lineHeight: 1.8 }}>
              Structured learning paths built for real careers. Pick your depth — from free roadmap courses to an intensive live sprint.
            </p>

            {/* Stats */}
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px 10px", marginBottom: 42 }}>
              {[["6,000+", "Alumni"], ["3", "Free Roadmaps"], ["4.8★", "Avg Rating"], ["Live", "Q&A Sessions"]].map(([v, l]) => (
                <div key={l} style={{ textAlign: "center", padding: "12px 22px", background: statBg, border: `1px solid ${statBorder}`, borderRadius: 12 }}>
                  <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(1.1rem, 2.5vw, 1.5rem)", fontWeight: 800, color: textPri }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: textMuted, fontWeight: 500, marginTop: 2 }}>{l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <div style={{ marginTop: 48, marginBottom: 0 }}>
            <GodLevelCourses />
          </div>
        </div>

        {/* ── COURSES ─────────────────────────────────────────────────────────── */}
        <Box py={16}>
          <Container maxW="7xl">
            <VStack spacing={3} mb={10}>
              <Text
                fontSize={["3xl", "4xl", "5xl"]}
                fontWeight="900"
                lineHeight="1.05"
                color={useColorModeValue("black", "white")}
                fontFamily="Bricolage Grotesque"
                textAlign="center"
                letterSpacing="tighter"
              >
                Learn Your Way. <span style={{ color: "#EF4444" }}>For Free.</span>
              </Text>
              <Text fontSize="md" color={useColorModeValue("gray.600", "gray.400")} textAlign="center" maxW="640px">
                Go at your own pace with our free roadmap courses — Java + DSA, Aptitude, and DBMS — or commit to a live 60-day sprint where we solve 300 LeetCode questions together with a real accountability structure.
              </Text>
            </VStack>

            {/* Smaller cards: 1 col on mobile, 2 on tablet, 4 on desktop */}
            <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing={5} alignItems="stretch">
              {FREE_COURSES.map((course) => (
                <FreeCourseCard
                  key={course.key}
                  course={course}
                  onViewRoadmap={() => setOpenCourseKey(course.key)}
                />
              ))}
              <DsaIntensiveCard />
            </SimpleGrid>
          </Container>
        </Box>
      </div>

      <AnimatePresence>
        {activeCourse && (
          <RoadmapModal
            course={activeCourse}
            onClose={() => setOpenCourseKey(null)}
            isDark={isDark}
          />
        )}
      </AnimatePresence>
    </>
  );
}